import React, { useState, useMemo } from "react";
import DataTable, {
  DataTableBody,
  DataTableHeader,
  DataTableCell,
  DataTableRow,
  DataTableHead,
} from "@amzn/meridian/data-table";
import Icon from "@amzn/meridian/icon";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import Button from "@amzn/meridian/button";
import { ShiftSummaryCategory } from "../../pages/reviewAndPublish/model";

interface ShiftDataTableStateProps {
  selectedCategory?: ShiftSummaryCategory;
}

interface ProcessedRowData {
  id: string;
  label: string;
  values: number[];
}

const ShiftDataTable: React.FC<ShiftDataTableStateProps> = ({
  selectedCategory,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const isRowExpanded = (rowId: string) => {
    return expandedRows.has(rowId);
  };

  const toggleRowExpansion = (rowId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  // Process selected category data to create table structure
  const { processedData, timeSlots, inboundTotals } = useMemo(() => {
    if (
      !selectedCategory ||
      !selectedCategory.planOutput ||
      selectedCategory.planOutput.length === 0
    ) {
      return { processedData: [], timeSlots: [], inboundTotals: [] };
    }

    const planOutput = selectedCategory.planOutput;

    // Extract time slots
    const timeSlots = planOutput.map(
      (slot) => `${slot.startTime} - ${slot.endTime}`
    );

    // Get all unique labels
    const allLabels = new Set<string>();
    planOutput.forEach((slot) => {
      slot.data.forEach((item) => {
        allLabels.add(item.label);
      });
    });

    // Create processed data structure
    const processedData: ProcessedRowData[] = Array.from(allLabels).map(
      (label) => {
        const id = label
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-]/g, "");
        const values: number[] = [];

        // For each time slot, find the value for this label
        planOutput.forEach((slot) => {
          const dataItem = slot.data.find((item) => item.label === label);
          values.push(dataItem ? dataItem.intValue : 0);
        });

        return {
          id,
          label,
          values,
        };
      }
    );

    // Calculate inbound totals for each time slot
    const inboundTotals: number[] = [];
    for (let i = 0; i < timeSlots.length; i++) {
      const total = processedData.reduce((sum, row) => sum + row.values[i], 0);
      inboundTotals.push(total);
    }

    return { processedData, timeSlots, inboundTotals };
  }, [selectedCategory]);

  const renderRow = () => {
    return processedData.map((dataRow) => {
      return (
        <DataTableRow key={dataRow.id}>
          <DataTableHeader>
            <Button
              type="icon"
              size="small"
              onClick={() => toggleRowExpansion(dataRow.id)}
            >
              <Icon
                tokens={
                  isRowExpanded(dataRow.id)
                    ? chevronDownSmallTokens
                    : chevronRightSmallTokens
                }
              />
            </Button>
            {dataRow.label}
          </DataTableHeader>
          {dataRow.values.map((value, index) => (
            <DataTableCell key={index}>{value % 1 === 0 ? value.toString() : value.toFixed(2)}</DataTableCell>
          ))}
        </DataTableRow>
      );
    });
  };

  return (
    <DataTable
      size="medium"
      showRowDividers={true}
      showColumnDividers={false}
      showStripes={false}
      showBorder={false}
    >
      <DataTableHead>
        <DataTableRow>
          <DataTableHeader>All</DataTableHeader>
          {timeSlots.map((timeSlot, index) => (
            <DataTableHeader key={index}>{timeSlot}</DataTableHeader>
          ))}
        </DataTableRow>
      </DataTableHead>

      <DataTableBody>
        <DataTableRow>
          <DataTableCell backgroundColor={"secondary"}>
            Inbound Totals
          </DataTableCell>
          {inboundTotals.map((total, index) => (
            <DataTableHeader key={index} backgroundColor={"secondary"}>
              {total % 1 === 0 ? total.toString() : total.toFixed(2)}
            </DataTableHeader>
          ))}
        </DataTableRow>
        {renderRow()}
      </DataTableBody>
    </DataTable>
  );
};

export default ShiftDataTable;
